import dayjs from 'dayjs';
import i18n from 'i18next';
import type { TFunction } from 'i18next';
import type {
  ExcelFileMetadata,
  ExcelTransaction,
  Organisation,
  TrackingCategory,
  ValidatedExcelTransaction,
} from 'store/organisation/models';
import { ReviewStatus, UploadStatus } from 'store/organisation/models';
import { normalizeExcelTransactionDateField } from 'store/organisation/reducer/excelHandlers';
import type { TaxRate } from 'store/returns/models/returnData';
import { ContactType } from 'store/app/enums';
import { Provider } from 'store/api/enums';
import { MessageStates } from 'containers/MessageBox';
import { turnMessageOn } from 'store/app/actions';
import {
  FILE_PANEL_WIDTH_MAX,
  FILE_PANEL_WIDTH_MIN,
  TRACKING_CATEGORY_1_COLUMN_KEY,
  TRACKING_CATEGORY_2_COLUMN_KEY,
  EDITABLE_ROW_KEYS,
  DOCUMENT_GROUP_SHARED_DRAWER_KEYS,
  OPTIONAL_NUMERIC_COLUMN_KEYS,
  TRACKING_OPTION_ID_FIELDS,
  TRACKING_TRANSACTION_FIELDS,
} from './constants';
import { parseLocaleNumber, toApiNumber, toApiQuantity } from 'utils/numberFormat';
import {
  buildAccountNameByIdMapFromOptions,
  findAccountOptionInLists,
  lookupAccountNameInCoa,
  resolveAccountSelectEditState,
  type AccountNameGroupOption,
} from './account-display';
import {
  buildTaxNameByIdMap,
  resolveTaxSelectEditState,
  syncTaxFieldsFromRateId,
} from './tax-display';
import {
  buildItemNameByIdMapFromOptions,
  findItemOptionInList,
  resolveItemSelectEditState,
  type TransactionItemOption,
} from './item-display';
import { isQuickBooksOrZohoProvider } from 'utils/get-provider-type';
import { filterSidePanelFiles } from './pending-rescan-drawer-reopen';

export type CaptureProviderFileContext = {
  isExcelProvider: boolean;
  isReceiptsOnlyProvider: boolean;
};

export function resolveCaptureProviderFileContext(
  providerName: string | undefined
): CaptureProviderFileContext {
  const provider = providerName?.toLowerCase() ?? '';
  return {
    isExcelProvider: provider === Provider.EXCEL.toLowerCase(),
    isReceiptsOnlyProvider: provider === Provider.RECEIPTS.toLowerCase(),
  };
}

/** Active capture files shown in the Transactions side panel (before tab/date/search filters). */
export function getActiveCaptureExcelFiles(
  excelFilesMetadata: ExcelFileMetadata[] | undefined
): ExcelFileMetadata[] {
  return (excelFilesMetadata ?? []).filter(
    (file) =>
      file.status === UploadStatus.ACTIVE &&
      (file.totalRows == null || Number(file.totalRows) > 0)
  );
}

export type CaptureFileTabCounts = {
  pendingReviewFiles: number;
  readyToPostFiles: number;
};

/**
 * File counts per capture workflow tab — same rules as the Transactions side panel
 * (`getFileIdsForStateTab` / `filterSidePanelFiles`, without date or search filters).
 */
export function computeCaptureFileCountsFromMetadata(
  excelFilesMetadata: ExcelFileMetadata[] | undefined,
  providerName: string | undefined
): CaptureFileTabCounts | undefined {
  const { isExcelProvider, isReceiptsOnlyProvider } =
    resolveCaptureProviderFileContext(providerName);
  if (isExcelProvider) return undefined;

  const excelFiles = getActiveCaptureExcelFiles(excelFilesMetadata);
  const filterOptions = { isReceiptsOnlyProvider, isExcelProvider };

  return {
    pendingReviewFiles: filterSidePanelFiles(excelFiles, {
      tab: 'CAPTURED',
      ...filterOptions,
    }).length,
    readyToPostFiles: filterSidePanelFiles(excelFiles, {
      tab: 'REVIEWED',
      ...filterOptions,
    }).length,
  };
}

/**
 * Pending-review total for the entity Transactions nav tab.
 * Matches Transactions tab "Pending Review (N)" (CAPTURED file-state total).
 */
export function computePendingReviewTransactionCount(
  excelFilesMetadata: ExcelFileMetadata[] | undefined,
  providerName: string | undefined
): number {
  const { isExcelProvider, isReceiptsOnlyProvider } =
    resolveCaptureProviderFileContext(providerName);

  if (isExcelProvider) return 0;

  const excelFiles = getActiveCaptureExcelFiles(excelFilesMetadata);

  let captured = 0;
  for (const file of excelFiles) {
    const rows =
      typeof file.totalRows === 'number'
        ? file.totalRows
        : Number(file.totalRows) || 0;
    const isReviewed =
      file.isReviewed === true ||
      (file as { isReviwed?: boolean }).isReviwed === true;

    if (isReceiptsOnlyProvider) {
      if (!isReviewed) captured += rows;
      continue;
    }

    if (file.isPosted === true) continue;
    if (!isReviewed) captured += rows;
  }

  return captured;
}

/** User-facing labels for required-field validation on edit save. */
export const TRANSACTION_EDIT_REQUIRED_FIELD_LABELS = {
  contactName: 'Name',
  taxName: 'Tax Name',
  transactionDate: 'Date',
  sourceType: 'Source',
  description: 'Description',
  gross: 'Total',
  net: 'Net',
  tax: 'Tax',
} as const;

export type TransactionEditRequiredFieldKey =
  keyof typeof TRANSACTION_EDIT_REQUIRED_FIELD_LABELS;

const TRANSACTION_EDIT_REQUIRED_FIELD_I18N_KEYS: Record<
  TransactionEditRequiredFieldKey,
  string
> = {
  contactName: 'transactions.contactName',
  taxName: 'transactions.taxName',
  transactionDate: 'transactions.transactionDate',
  sourceType: 'transactions.sourceType',
  description: 'transactions.description',
  gross: 'transactions.gross',
  net: 'transactions.net',
  tax: 'transactions.tax',
};

export function getTransactionEditRequiredFieldLabel(
  key: TransactionEditRequiredFieldKey,
  t: TFunction = i18n.t.bind(i18n)
): string {
  return t(TRANSACTION_EDIT_REQUIRED_FIELD_I18N_KEYS[key]);
}

export type TransactionEditSaveValidationOptions = {
  /** QuickBooks, Zoho, Xero, etc. */
  requireDescription: boolean;
  /** Excel and Receipts store tax name as taxRateId. */
  useTaxRateIdForTaxName: boolean;
};

function isBlankText(value: unknown): boolean {
  return value == null || String(value).trim() === '';
}

function isBlankNumeric(value: unknown): boolean {
  if (value == null || value === '') return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  const n =
    typeof value === 'number' ? value : parseLocaleNumber(String(value));
  return Number.isNaN(n);
}

function parseDrawerAmountTotal(value: unknown): number {
  if (value == null || String(value).trim() === '') return 0;
  const n =
    typeof value === 'number' ? value : parseLocaleNumber(String(value));
  return Number.isFinite(n) ? n : 0;
}

/** Sum net / tax / gross across document lines using current drawer draft values. */
export function sumDocumentDrawerAmountTotals(
  rows: ValidatedExcelTransaction[],
  draftMap: Record<string, Partial<ValidatedExcelTransaction>>
): { net: number; tax: number; gross: number } {
  let net = 0;
  let tax = 0;
  let gross = 0;
  for (const row of rows) {
    if (!row.uuid) continue;
    const draft = { ...row, ...(draftMap[String(row.uuid)] ?? {}) };
    const taxVal = parseDrawerAmountTotal(draft.tax);
    tax += taxVal;

    const hasQuantityOrUnitPrice =
      draft.quantity != null || draft.unitPrice != null;
    if (hasQuantityOrUnitPrice) {
      const calculated = resolveCalculatedGrossAndNet(draft);
      net += calculated.net;
      gross += calculated.gross;
      continue;
    }

    const netVal = parseDrawerAmountTotal(draft.net);
    net += netVal;
    const grossRaw = draft.gross;
    const grossVal =
      grossRaw != null && String(grossRaw).trim() !== ''
        ? parseDrawerAmountTotal(grossRaw)
        : netVal + taxVal;
    gross += grossVal;
  }
  return { net, tax, gross };
}

/** Currency for page totals: single row currency when uniform, else entity fallback. */
export function resolveTransactionTotalsCurrencyCode(
  rows: ValidatedExcelTransaction[],
  draftMap: Record<string, Partial<ValidatedExcelTransaction>>,
  fallbackCurrency?: string | null
): string | undefined {
  const codes = new Set<string>();
  for (const row of rows) {
    if (!row.uuid) continue;
    const draft = draftMap[String(row.uuid)] ?? row;
    const code = String(draft.currency ?? '').trim();
    if (code) codes.add(code.toUpperCase());
  }
  if (codes.size === 1) return [...codes][0];
  const fallback = String(fallbackCurrency ?? '').trim();
  return fallback ? fallback.toUpperCase() : undefined;
}

/** Returns missing required drawer field keys (empty = valid). */
export function validateTransactionEditSaveFieldKeys(
  body: ExcelTransaction,
  options: TransactionEditSaveValidationOptions
): TransactionEditRequiredFieldKey[] {
  const missing: TransactionEditRequiredFieldKey[] = [];

  if (isBlankText(body.contactId) && isBlankText(body.contactName)) {
    missing.push('contactName');
  }

  // Tax Name is edited through the organisation tax-rate dropdown, whose
  // submitted value is taxRateId. Do not let a stale display taxName satisfy
  // validation after the user clears the dropdown.
  if (isBlankText(body.taxRateId)) missing.push('taxName');

  if (
    isBlankText(body.transactionDate) ||
    !dayjs(body.transactionDate).isValid()
  ) {
    missing.push('transactionDate');
  }

  //if (isBlankText(body.sourceType)) missing.push('sourceType');

  if (options.requireDescription && isBlankText(body.description)) {
    missing.push('description');
  }

  if (isBlankNumeric(body.gross)) missing.push('gross');
  if (isBlankNumeric(body.net)) missing.push('net');
  if (isBlankNumeric(body.tax)) missing.push('tax');

  return missing;
}

/** Net / Total on the body that would be saved. Blank values are not negative. */
export function listNegativeNetOrGrossKeys(
  body: Pick<ExcelTransaction, 'net' | 'gross'>
): Array<'net' | 'gross'> {
  const negative: Array<'net' | 'gross'> = [];
  const net = parseTransactionNumericField(body.net);
  const gross = parseTransactionNumericField(body.gross);
  if (net != null && net < 0) negative.push('net');
  if (gross != null && gross < 0) negative.push('gross');
  return negative;
}

const NEGATIVE_AMOUNT_MESSAGE_PREFIX = '\u0000neg:';

function formatNegativeNetOrGrossMessage(
  keys: Array<'net' | 'gross'>
): string {
  const labels = keys.map((key) => getTransactionEditRequiredFieldLabel(key));
  if (labels.length === 1) {
    return i18n.t('organisation.negative-amount-one', { field: labels[0] });
  }
  return i18n.t('organisation.negative-amount-many', {
    fields: labels.join(', '),
  });
}

function encodeNegativeAmountMessage(message: string): string {
  return `${NEGATIVE_AMOUNT_MESSAGE_PREFIX}${message}`;
}

function splitRequiredAndNegativeLabels(labels: string[]): {
  requiredLabels: string[];
  negativeMessages: string[];
} {
  const requiredLabels: string[] = [];
  const negativeMessages: string[] = [];
  for (const label of labels) {
    if (label.startsWith(NEGATIVE_AMOUNT_MESSAGE_PREFIX)) {
      negativeMessages.push(label.slice(NEGATIVE_AMOUNT_MESSAGE_PREFIX.length));
    } else {
      requiredLabels.push(label);
    }
  }
  return { requiredLabels, negativeMessages };
}

/** Returns missing required field labels, plus a block when Net or Total is negative. */
export function validateTransactionEditSave(
  body: ExcelTransaction,
  options: TransactionEditSaveValidationOptions
): string[] {
  const labels = validateTransactionEditSaveFieldKeys(body, options).map(
    (key) => getTransactionEditRequiredFieldLabel(key)
  );
  const negativeKeys = listNegativeNetOrGrossKeys(body);
  if (negativeKeys.length > 0) {
    labels.push(
      encodeNegativeAmountMessage(formatNegativeNetOrGrossMessage(negativeKeys))
    );
  }
  return labels;
}

export function formatTransactionEditValidationMessage(
  missingLabels: string[]
): string {
  const { requiredLabels, negativeMessages } =
    splitRequiredAndNegativeLabels(missingLabels);
  const parts: string[] = [];
  if (requiredLabels.length === 1) {
    parts.push(
      i18n.t('organisation.required-field-one', { field: requiredLabels[0] })
    );
  } else if (requiredLabels.length > 1) {
    parts.push(
      i18n.t('organisation.required-fields-many', {
        fields: requiredLabels.join(', '),
      })
    );
  }
  parts.push(...negativeMessages);
  return parts.join(' ');
}

export type TrackingOptionIdField = (typeof TRACKING_OPTION_ID_FIELDS)[number];

const TRACKING_DIMENSION_FIELDS = [
  {
    optionId: 'trackingOption1Id',
    optionName: 'trackingOption1Name',
    categoryId: 'trackingCategory1Id',
    categoryName: 'trackingCategory1Name',
  },
  {
    optionId: 'trackingOption2Id',
    optionName: 'trackingOption2Name',
    categoryId: 'trackingCategory2Id',
    categoryName: 'trackingCategory2Name',
  },
] as const;

export function trackingOptionIdFieldToDimensionIndex(
  field: TrackingOptionIdField
): 0 | 1 {
  return field === 'trackingOption1Id' ? 0 : 1;
}

export function isTrackingOptionIdField(
  field: string
): field is TrackingOptionIdField {
  return field === 'trackingOption1Id' || field === 'trackingOption2Id';
}

/**
 * When the user picks a tracking option, populate category id/name and option name
 * from org tracking categories (backend rows may omit category fields when blank).
 */
export function applyTrackingOptionSelectionToTransactionFields(
  optionField: TrackingOptionIdField,
  optionId: string | null | undefined,
  trackingCategories: TrackingCategory[]
): Partial<ExcelTransaction> {
  const dim = trackingOptionIdFieldToDimensionIndex(optionField);
  const fields = TRACKING_DIMENSION_FIELDS[dim];
  const category = trackingCategories[dim];
  const trimmedId =
    optionId == null || String(optionId).trim() === ''
      ? ''
      : String(optionId).trim();

  if (!trimmedId) {
    return {
      [fields.optionId]: null,
      [fields.optionName]: null,
      [fields.categoryId]: null,
      [fields.categoryName]: null,
    };
  }

  const option =
    (category?.options ?? []).find(
      (o) => String(o.id).trim() === trimmedId
    ) ??
    trackingCategories
      .flatMap((item) => item.options ?? [])
      .find((o) => String(o.id).trim() === trimmedId);
  const categoryId = category?.id != null ? String(category.id).trim() : '';
  const categoryName = category?.name?.trim() ?? '';

  return {
    [fields.optionId]: trimmedId,
    [fields.optionName]: option?.name?.trim() || null,
    [fields.categoryId]: categoryId || null,
    [fields.categoryName]: categoryName || null,
  };
}

/** Fill missing category/option names on save when only option ids were edited. */
export function syncTrackingFieldsFromCategories(
  body: ExcelTransaction,
  trackingCategories: TrackingCategory[]
): ExcelTransaction {
  if (!trackingCategories.length) return body;
  const record = { ...body } as ExcelTransaction & Record<string, unknown>;

  for (let dim = 0; dim < TRACKING_DIMENSION_FIELDS.length; dim++) {
    const fields = TRACKING_DIMENSION_FIELDS[dim];
    const optionId = String(record[fields.optionId] ?? '').trim();
    if (!optionId) continue;

    const category = trackingCategories[dim];
    if (!category) continue;

    if (!String(record[fields.categoryId] ?? '').trim()) {
      const catId = category.id != null ? String(category.id).trim() : '';
      if (catId) record[fields.categoryId] = catId;
    }
    if (!String(record[fields.categoryName] ?? '').trim()) {
      const catName = category.name?.trim() ?? '';
      if (catName) record[fields.categoryName] = catName;
    }
    if (!String(record[fields.optionName] ?? '').trim()) {
      const option =
        (category.options ?? []).find(
          (o) => String(o.id).trim() === optionId
        ) ??
        trackingCategories
          .flatMap((item) => item.options ?? [])
          .find((o) => String(o.id).trim() === optionId);
      const optName = option?.name?.trim() ?? '';
      if (optName) record[fields.optionName] = optName;
    }
  }

  return record as ExcelTransaction;
}

export type BuildTransactionSaveBodyParams = {
  original: ExcelTransaction;
  draft: Partial<ExcelTransaction>;
  showTrackingCategoryColumns: boolean;
  trackingCategories?: TrackingCategory[];
  isAccountIdProvider: boolean;
  accountNameByIdMap: Map<string, string>;
  accountNameOptions: { label: string; value: string }[];
  accountNameOptionsGrouped?: AccountNameGroupOption[];
  itemNameByIdMap?: Map<string, string>;
  transactionItemOptions?: TransactionItemOption[];
  taxRateByIdMap: Map<string, TaxRate>;
  /** Keep these fields from `original` (not edited in drawer). */
  preserveOriginalKeys?: readonly (keyof ExcelTransaction)[];
  /**
   * Xero / QuickBooks / Zoho / Receipts: ignore draft/API gross & net; derive from
   * qty / unitPrice / discount / tax (and taxInclusive).
   */
  deriveGrossNetFromQuantityUnitPrice?: boolean;
};

/** Merge draft into original, normalize, and sync derived fields before save. */
export function buildTransactionSaveBody(
  params: BuildTransactionSaveBodyParams
): ExcelTransaction {
  const {
    original,
    draft,
    showTrackingCategoryColumns,
    trackingCategories = [],
    accountNameByIdMap,
    accountNameOptions,
    accountNameOptionsGrouped = [],
    itemNameByIdMap = new Map(),
    transactionItemOptions = [],
    taxRateByIdMap,
    preserveOriginalKeys,
    deriveGrossNetFromQuantityUnitPrice = false,
  } = params;

  let body: ExcelTransaction = {
    ...original,
    ...draft,
  };
  if (preserveOriginalKeys?.length) {
    const orig = original as unknown as Record<string, unknown>;
    const out = body as unknown as Record<string, unknown>;
    for (const key of preserveOriginalKeys) {
      if (Object.prototype.hasOwnProperty.call(orig, key)) {
        out[key as string] = orig[key as string];
      }
    }
  }
  body = normalizeExcelTransactionNumericFields(body);
  body = normalizeExcelTransactionDateFields(body);

  const taxRateSelectionWasCleared =
    Object.prototype.hasOwnProperty.call(draft, 'taxRateId') &&
    (draft.taxRateId == null || String(draft.taxRateId).trim() === '');
  if (taxRateSelectionWasCleared) {
    body.taxRateId = null;
    body.taxName = '';
    body.taxRate = 0;
  }

  if (body.accountId != null && String(body.accountId).trim() !== '') {
    const resolvedName = lookupAccountNameInCoa(
      String(body.accountId).trim(),
      accountNameByIdMap,
      accountNameOptions
    );
    if (resolvedName) {
      body.accountName = resolvedName;
    }
  } else if (
    Object.prototype.hasOwnProperty.call(draft, 'accountCode') ||
    Object.prototype.hasOwnProperty.call(draft, 'accountName')
  ) {
    const nameCandidate = String(
      body.accountCode ?? body.accountName ?? ''
    ).trim();
    if (nameCandidate) {
      const matched = findAccountOptionInLists(
        nameCandidate,
        accountNameOptions,
        accountNameOptionsGrouped
      );
      if (
        matched &&
        (accountNameByIdMap.has(matched.value) ||
          accountNameByIdMap.has(matched.value.toLowerCase()))
      ) {
        body.accountId = matched.value;
        body.accountName = matched.label;
        body.accountCode = matched.value;
      }
    }
  }

  if (Object.prototype.hasOwnProperty.call(draft, 'itemId')) {
    const candidate = String(body.itemId ?? '').trim();
    if (!candidate) {
      body.itemId = null;
    } else {
      const matched = findItemOptionInList(candidate, transactionItemOptions);
      if (
        matched &&
        (itemNameByIdMap.has(matched.value) ||
          itemNameByIdMap.has(matched.value.toLowerCase()))
      ) {
        body.itemId = matched.value;
      }
    }
  }

  body = syncTaxFieldsFromRateId(body, taxRateByIdMap);

  if (deriveGrossNetFromQuantityUnitPrice) {
    body = applyCalculatedGrossAndNet(body);
  }

  const bodyExtras = body as unknown as Record<string, unknown>;
  if (!showTrackingCategoryColumns) {
    for (const f of TRACKING_TRANSACTION_FIELDS) {
      delete bodyExtras[f];
    }
  } else {
    body = syncTrackingFieldsFromCategories(body, trackingCategories);
    const syncedExtras = body as unknown as Record<string, unknown>;
    for (const f of TRACKING_TRANSACTION_FIELDS) {
      const v = syncedExtras[f];
      if (v === '') syncedExtras[f] = null;
    }
  }

  return body;
}

export function hasRowChanges(
  draft: Partial<ValidatedExcelTransaction> | null,
  original:
    | ValidatedExcelTransaction
    | Record<string, unknown>
    | null
    | undefined
): boolean {
  if (!draft || !original) return false;
  const origR = original as unknown as Record<string, unknown>;
  const effectiveDraft = {
    ...origR,
    ...(draft as unknown as Record<string, unknown>),
  };
  for (const k of TRACKING_OPTION_ID_FIELDS) {
    const dStr = normalizeComparableFieldValue(k, effectiveDraft[k]);
    const oStr = normalizeComparableFieldValue(k, origR[k]);
    if (dStr !== oStr) return true;
  }
  return EDITABLE_ROW_KEYS.some((k) => {
    const dStr = normalizeComparableFieldValue(k, effectiveDraft[k]);
    const oStr = normalizeComparableFieldValue(k, origR[k]);
    return dStr !== oStr;
  });
}

/**
 * Diff document-shared fields between two drafts (drawer Document details keys).
 * Includes `contactId` whenever it changes with `contactName`.
 */
export function extractDocumentSharedFieldPatch(
  before: Partial<ValidatedExcelTransaction>,
  after: Partial<ValidatedExcelTransaction>
): Partial<ValidatedExcelTransaction> | null {
  const beforeR = before as Record<string, unknown>;
  const afterR = after as Record<string, unknown>;
  const patch: Record<string, unknown> = {};

  for (const key of DOCUMENT_GROUP_SHARED_DRAWER_KEYS) {
    if (!Object.is(beforeR[key], afterR[key])) {
      patch[key] = afterR[key];
    }
  }
  if (!Object.is(beforeR.contactId, afterR.contactId)) {
    patch.contactId = afterR.contactId;
  } else if (
    Object.prototype.hasOwnProperty.call(patch, 'contactName') &&
    !Object.prototype.hasOwnProperty.call(patch, 'contactId')
  ) {
    patch.contactId = afterR.contactId;
  }

  return Object.keys(patch).length > 0
    ? (patch as Partial<ValidatedExcelTransaction>)
    : null;
}

const DOCUMENT_SHARED_COMPARE_KEYS = new Set<string>([
  ...DOCUMENT_GROUP_SHARED_DRAWER_KEYS,
  'contactId',
]);

/**
 * True when editable per-line fields differ (excludes document-shared drawer keys).
 * When totals are derived from qty/unitPrice/taxInclusive, gross/net are ignored.
 */
export function hasPerLineRowChanges(
  draft: Partial<ValidatedExcelTransaction> | null,
  original:
    | ValidatedExcelTransaction
    | Record<string, unknown>
    | null
    | undefined,
  options?: { treatGrossNetAsDerived?: boolean }
): boolean {
  if (!draft || !original) return false;
  const origR = original as unknown as Record<string, unknown>;
  const effectiveDraft = {
    ...origR,
    ...(draft as unknown as Record<string, unknown>),
  };
  const skip = new Set(DOCUMENT_SHARED_COMPARE_KEYS);
  if (options?.treatGrossNetAsDerived) {
    skip.add('gross');
    skip.add('net');
  }
  for (const k of TRACKING_OPTION_ID_FIELDS) {
    const dStr = normalizeComparableFieldValue(k, effectiveDraft[k]);
    const oStr = normalizeComparableFieldValue(k, origR[k]);
    if (dStr !== oStr) return true;
  }
  return EDITABLE_ROW_KEYS.some((k) => {
    if (skip.has(k)) return false;
    const dStr = normalizeComparableFieldValue(k, effectiveDraft[k]);
    const oStr = normalizeComparableFieldValue(k, origR[k]);
    return dStr !== oStr;
  });
}

/**
 * Mass-update body for document-shared fields (excludes taxInclusive — totals
 * must be recalculated per line via full-body batch save).
 * contactName maps to contactId (same as the mass-update bar).
 */
export function buildDocumentSharedMassUpdatePayload(
  sharedPatch: Partial<ValidatedExcelTransaction>
): Record<string, unknown> | null {
  const patch = sharedPatch as Record<string, unknown>;
  const updates: Record<string, unknown> = {};

  for (const key of DOCUMENT_GROUP_SHARED_DRAWER_KEYS) {
    if (key === 'taxInclusive' || key === 'contactName') continue;
    if (!Object.prototype.hasOwnProperty.call(patch, key)) continue;
    const value = patch[key];
    if (key === 'transactionDate' || key === 'dueDate' || key === 'uploadDate') {
      const normalised = normalizeExcelTransactionDateField(value);
      updates[key] = normalised ?? value;
    } else if (key === 'exchangeRate') {
      if (value === '' || value == null) {
        updates[key] = value;
      } else {
        const n = parseTransactionNumericField(value);
        updates[key] = n ?? value;
      }
    } else {
      updates[key] = value;
    }
  }

  if (
    Object.prototype.hasOwnProperty.call(patch, 'contactId') ||
    Object.prototype.hasOwnProperty.call(patch, 'contactName')
  ) {
    const contactId = patch.contactId;
    if (contactId != null && String(contactId).trim() !== '') {
      updates.contactId = contactId;
    }
  }

  return Object.keys(updates).length > 0 ? updates : null;
}

export function trackingTableColumnKeyToOptionField(
  colKey: string
): (typeof TRACKING_OPTION_ID_FIELDS)[number] | null {
  if (colKey === TRACKING_CATEGORY_1_COLUMN_KEY) return 'trackingOption1Id';
  if (colKey === TRACKING_CATEGORY_2_COLUMN_KEY) return 'trackingOption2Id';
  return null;
}

export function clampFilePanelWidth(w: number): number {
  return Math.round(
    Math.min(FILE_PANEL_WIDTH_MAX, Math.max(FILE_PANEL_WIDTH_MIN, w))
  );
}

/** Normalise transactionDate / dueDate on save bodies. */
export function normalizeExcelTransactionDateFields(
  body: ExcelTransaction
): ExcelTransaction {
  const out = { ...body } as ExcelTransaction & Record<string, unknown>;
  if (
    Object.prototype.hasOwnProperty.call(out, 'transactionDate') &&
    out.transactionDate != null &&
    out.transactionDate !== ''
  ) {
    const normalised = normalizeExcelTransactionDateField(out.transactionDate);
    if (normalised) out.transactionDate = normalised;
  }
  if (Object.prototype.hasOwnProperty.call(out, 'dueDate')) {
    out.dueDate = normalizeExcelTransactionDateField(out.dueDate);
  }
  return out as ExcelTransaction;
}

function normalizeComparableFieldValue(key: string, value: unknown): string {
  if (value == null || value === '') return '';
  if (key === 'transactionDate' || key === 'dueDate') {
    const d = dayjs(value as string | number | Date);
    return d.isValid() ? d.format('YYYY-MM-DD') : String(value).trim();
  }
  return String(value).trim();
}

/** Ensure numeric fields are sent as numbers with dot decimal for backend compatibility. */
export function normalizeExcelTransactionNumericFields(
  body: ExcelTransaction
): ExcelTransaction {
  const out = { ...body };
  for (const key of [
    'gross',
    'net',
    'tax',
    'taxRate',
    'exchangeRate',
    'discountAmount',
    'quantity',
    'unitPrice',
  ] as const) {
    const v = (out as Record<string, unknown>)[key];
    if (v === '' || v === '-') {
      if (OPTIONAL_NUMERIC_COLUMN_KEYS.has(key)) {
        (out as Record<string, unknown>)[key] = null;
      }
      continue;
    }
    if (
      (typeof v === 'string' || typeof v === 'number') &&
      v !== undefined &&
      v !== null
    ) {
      const parsed = parseLocaleNumber(v);
      const num =
        key === 'quantity' ? toApiQuantity(parsed) : toApiNumber(parsed);
      (out as Record<string, unknown>)[key] = num;
    }
  }
  return out;
}

/**
 * Parse a transaction numeric field (supports in-progress edit strings).
 * Empty / invalid → null.
 */
export function parseTransactionNumericField(value: unknown): number | null {
  if (value == null || value === '' || value === '-') return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const num = parseLocaleNumber(value);
  return Number.isFinite(num) ? num : null;
}

/**
 * Xero / QuickBooks / Zoho / Receipts totals.
 * Tax inclusive: Total = qty × unitPrice − discount
 * Tax exclusive: Total = qty × unitPrice + tax − discount
 * Net is always Total − tax.
 * Missing `taxInclusive` defaults to inclusive (legacy behaviour).
 */
export function resolveCalculatedGrossAndNet(fields: {
  quantity?: unknown;
  unitPrice?: unknown;
  discountAmount?: unknown;
  tax?: unknown;
  taxInclusive?: unknown;
}): { gross: number; net: number } {
  const quantity = parseTransactionNumericField(fields.quantity) ?? 0;
  const unitPrice = parseTransactionNumericField(fields.unitPrice) ?? 0;
  const discount = parseTransactionNumericField(fields.discountAmount) ?? 0;
  const tax = parseTransactionNumericField(fields.tax) ?? 0;
  const taxInclusive = fields.taxInclusive !== false;
  const line = quantity * unitPrice;
  const gross = toApiNumber(
    taxInclusive ? line - discount : line + tax - discount
  );
  const net = toApiNumber(gross - tax);
  return { gross, net };
}

/** Apply calculated Total / Net onto a transaction body (mutates a shallow copy). */
export function applyCalculatedGrossAndNet<
  T extends {
    quantity?: unknown;
    unitPrice?: unknown;
    discountAmount?: unknown;
    tax?: unknown;
    taxInclusive?: unknown;
    gross?: unknown;
    net?: unknown;
  }
>(row: T): T {
  const { gross, net } = resolveCalculatedGrossAndNet(row);
  return { ...row, gross, net };
}

export type TransactionEditDrawerColumnCount = 3 | 4 | 5 | 6;

/** Segmented callbacks can emit string — avoid NaN/`Invalid array length` in column chunks. */
export function normalizeTransactionEditDrawerColumnCount(
  raw: string | number
): TransactionEditDrawerColumnCount {
  const n = typeof raw === 'number' ? raw : Number.parseInt(String(raw), 10);
  if (Number.isFinite(n) && (n === 3 || n === 4 || n === 5 || n === 6)) {
    return n;
  }
  return 3;
}

export function transactionEditDrawerGridTemplateColumns(
  columnCount: TransactionEditDrawerColumnCount
): string {
  return `repeat(${columnCount}, 1fr)`;
}

type EditableRowKey = (typeof EDITABLE_ROW_KEYS)[number];

export type ResolveDrawerFieldTooltipTitleParams = {
  fieldKey: EditableRowKey;
  originalRow: ValidatedExcelTransaction;
  draftRow: Partial<ValidatedExcelTransaction>;
  isAccountIdProvider: boolean;
  /** COA dropdown values are account ids (Excel/Receipts/Xero/QBO). */
  coaAccountOptionsUseIds?: boolean;
  sourceTypeOptions: { label: string; value: string }[];
  contactOptions: { label: string; value: string }[];
  taxNameOptions: { label: string; value: string }[];
  accountNameOptions: { label: string; value: string }[];
  accountNameOptionsGrouped: {
    label: string;
    options: { label: string; value: string }[];
  }[];
  itemNameByIdMap?: Map<string, string>;
  transactionItemOptions?: TransactionItemOption[];
  organisationTaxRates: TaxRate[];
};

/** Full display text for hover when line-field controls truncate. */
export function resolveDrawerFieldTooltipTitle({
  fieldKey,
  originalRow,
  draftRow,
  isAccountIdProvider,
  coaAccountOptionsUseIds = false,
  sourceTypeOptions,
  contactOptions,
  taxNameOptions,
  accountNameOptions,
  accountNameOptionsGrouped,
  itemNameByIdMap = new Map(),
  transactionItemOptions = [],
  organisationTaxRates,
}: ResolveDrawerFieldTooltipTitleParams): string {
  const draft = draftRow as ValidatedExcelTransaction;
  const value = (draftRow as Record<string, unknown>)[fieldKey];
  const accountIdProvider = isAccountIdProvider || coaAccountOptionsUseIds;

  const optionLabel = (
    options: { label: string; value: string }[],
    raw: unknown
  ): string => {
    if (raw == null || raw === '') return '';
    const trimmed = String(raw).trim();
    const match =
      options.find((o) => String(o.value) === trimmed) ??
      options.find((o) => String(o.label) === trimmed);
    return match?.label != null ? String(match.label) : trimmed;
  };

  if (fieldKey === 'transactionDate' || fieldKey === 'dueDate') {
    const raw =
      value ??
      (originalRow as Record<string, unknown>)[fieldKey] ??
      (fieldKey === 'transactionDate'
        ? originalRow.transactionDate
        : (originalRow as { dueDate?: unknown }).dueDate);
    if (raw == null || raw === '') return '';
    const d = dayjs(raw as string | number | Date);
    return d.isValid() ? d.format('DD/MM/YYYY') : String(raw);
  }
  if (fieldKey === 'sourceType') {
    return optionLabel(sourceTypeOptions, value ?? originalRow.sourceType);
  }
  if (fieldKey === 'contactName') {
    const orig = originalRow as unknown as Record<string, unknown>;
    const rawVal =
      (value as string) ??
      (orig.contactId as string) ??
      (orig.contactName as string);
    return optionLabel(contactOptions, rawVal);
  }
  if (fieldKey === 'accountCode') {
    const orig = originalRow as unknown as Record<string, unknown>;
    const draftRecord = draftRow as unknown as Record<string, unknown>;
    const rowFields = {
      accountId: (draftRecord?.accountId ?? orig?.accountId) as string | null,
      accountCode: (draftRecord?.accountCode ?? orig?.accountCode) as
        | string
        | null,
      accountName: (draftRecord?.accountName ?? orig?.accountName) as
        | string
        | null,
    };
    const cellForResolve =
      draftRecord?.accountId ?? draftRecord?.accountCode ?? value;
    const { label } = resolveAccountSelectEditState({
      row: rowFields,
      cellVal: cellForResolve,
      isAccountIdProvider: accountIdProvider,
      accountNameByIdMap: buildAccountNameByIdMapFromOptions(
        accountNameOptions,
        accountNameOptionsGrouped
      ),
      accountNameOptions,
      accountNameOptionsGrouped,
    });
    return (
      label ??
      (rowFields.accountName != null ? String(rowFields.accountName) : '')
    );
  }
  if (fieldKey === 'itemId') {
    const orig = originalRow as unknown as Record<string, unknown>;
    const draftRecord = draftRow as unknown as Record<string, unknown>;
    const rowFields = {
      itemId: (draftRecord?.itemId ?? orig?.itemId) as string | null,
    };
    const cellForResolve = draftRecord?.itemId ?? value;
    const { label } = resolveItemSelectEditState({
      row: rowFields,
      cellVal: cellForResolve,
      itemNameByIdMap:
        itemNameByIdMap.size > 0
          ? itemNameByIdMap
          : buildItemNameByIdMapFromOptions(transactionItemOptions),
      transactionItemOptions,
    });
    return label ?? '';
  }
  if (fieldKey === 'taxName') {
    const { label } = resolveTaxSelectEditState({
      row: originalRow,
      draftRow: draft,
      cellVal: value,
      taxNameByIdMap: buildTaxNameByIdMap(organisationTaxRates),
      taxNameOptions,
    });
    return label ?? String(draft.taxName ?? originalRow.taxName ?? '');
  }
  if (value == null || value === '') {
    const fallback = (originalRow as Record<string, unknown>)[fieldKey];
    return fallback == null || fallback === '' ? '' : String(fallback);
  }
  return String(value);
}

export type TransactionDrawerSavePayload = {
  transactionId: string;
  original: ValidatedExcelTransaction;
  draft: Partial<ValidatedExcelTransaction>;
  markAsReviewed?: boolean;
};

/** Receipts review API — only when the row is not already reviewed. */
export function transactionQualifiesForMarkAsReviewed(
  row: Pick<ValidatedExcelTransaction, 'reviewStatus'>
): boolean {
  return (
    row.reviewStatus === ReviewStatus.NOT_REVIEWED ||
    row.reviewStatus === ReviewStatus.NONE
  );
}

export function getTransactionReversalType(
  row:
    | Pick<ValidatedExcelTransaction, 'reversalType'>
    | { reversalType?: unknown }
    | null
    | undefined
): string {
  return String(row?.reversalType ?? 'NONE').toUpperCase();
}

/** Soft-deleted row (`reversalType === 'DELETED'`). */
export function isTransactionDeletedRow(
  row:
    | Pick<ValidatedExcelTransaction, 'reversalType'>
    | { reversalType?: unknown }
    | null
    | undefined
): boolean {
  return getTransactionReversalType(row) === 'DELETED';
}

/**
 * Whether same-document rows are ready for edit/review drawer open.
 * After a line delete, file metadata `totalRows` can stay stale — if we already
 * fetched at the full-file page size, accept the live rows that came back.
 */
export function isDocumentFileFullyLoadedForEdit(
  loadedCount: number,
  targetFileRowCount: number | null | undefined,
  options?: { fetchedAtFullFilePageSize?: boolean }
): boolean {
  if (targetFileRowCount == null) return true;
  if (loadedCount >= targetFileRowCount) return true;
  return Boolean(options?.fetchedAtFullFilePageSize && loadedCount > 0);
}

/**
 * Live row that has been changed at least once:
 * `reversalType === NONE` and (journalModifiedDate or linkedTransactionId set).
 */
export function isTransactionEditedRow(
  row:
    | Pick<
        ValidatedExcelTransaction,
        'reversalType' | 'journalModifiedDate' | 'linkedTransactionId'
      >
    | {
        reversalType?: unknown;
        journalModifiedDate?: unknown;
        linkedTransactionId?: unknown;
      }
    | null
    | undefined
): boolean {
  if (!row || getTransactionReversalType(row) !== 'NONE') return false;
  const modified = row.journalModifiedDate;
  const linked = row.linkedTransactionId;
  const hasModified = modified != null && String(modified).trim() !== '';
  const hasLinked = linked != null && String(linked).trim() !== '';
  return hasModified || hasLinked;
}

export function formatJournalModifiedDateForDisplay(
  value: unknown
): string | null {
  if (value == null || value === '') return null;
  const d = dayjs(value as string | number | Date);
  if (d.isValid()) {
    return d.format('DD/MM/YYYY');
  }
  const asDateOnly = normalizeExcelTransactionDateField(value);
  if (asDateOnly) {
    const parsed = dayjs(asDateOnly);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : asDateOnly;
  }
  return String(value);
}

export type PreparedDrawerSaveItem = {
  transactionUuid: string;
  body: ExcelTransaction;
  markAsReviewed: boolean;
};

export type DrawerSaveLineValidationFailure = {
  lineContext: string;
  missingLabels: string[];
  transactionId: string;
  missingFieldKeys: TransactionEditRequiredFieldKey[];
};

export type TransactionDrawerFieldErrorsByTransactionId = Record<
  string,
  TransactionEditRequiredFieldKey[]
>;

export type TransactionDrawerBulkSaveResult =
  | { ok: true }
  | {
      ok: false;
      fieldErrorsByTransactionId: TransactionDrawerFieldErrorsByTransactionId;
    };

/** Label for validation errors when saving multiple drawer lines. */
export function getDrawerSaveLineContextLabel(
  body: ExcelTransaction,
  lineIndex: number,
  lineCount: number
): string {
  const ref = [body.transactionDate, body.transactionNumber]
    .filter((v) => v != null && String(v).trim() !== '')
    .map((v) => String(v).trim())
    .join(' · ');
  if (lineCount > 1) {
    return ref
      ? i18n.t('organisation.drawer-save-line-label-with-ref', {
          n: lineIndex + 1,
          ref,
        })
      : i18n.t('organisation.drawer-save-line-label', { n: lineIndex + 1 });
  }
  return ref;
}

export function formatDrawerBatchValidationMessage(
  failures: DrawerSaveLineValidationFailure[]
): string {
  if (failures.length === 0) return '';
  if (failures.length === 1 && !failures[0].lineContext) {
    return formatTransactionEditValidationMessage(failures[0].missingLabels);
  }
  return failures
    .map((f) => {
      const fieldMsg = formatTransactionEditValidationMessage(f.missingLabels);
      return f.lineContext ? `${f.lineContext}: ${fieldMsg}` : fieldMsg;
    })
    .join('\n');
}

export type PrepareDrawerTransactionSaveBatchParams = {
  payloads: TransactionDrawerSavePayload[];
  bodyParams: Omit<BuildTransactionSaveBodyParams, 'original' | 'draft'>;
  validationOptions: TransactionEditSaveValidationOptions;
  /** e.g. auto-mark reviewed on save when row was not yet reviewed. */
  enhanceBody?: (
    body: ExcelTransaction,
    original: ExcelTransaction
  ) => ExcelTransaction;
  resolveMarkAsReviewed?: (payload: TransactionDrawerSavePayload) => boolean;
};

/**
 * Build save bodies and validate every drawer line before any API call.
 * Shared document fields must already be on each line's draft (drawer syncs them).
 */
export function prepareDrawerTransactionSaveBatch({
  payloads,
  bodyParams,
  validationOptions,
  enhanceBody,
  resolveMarkAsReviewed,
}: PrepareDrawerTransactionSaveBatchParams):
  | { ok: true; items: PreparedDrawerSaveItem[] }
  | {
      ok: false;
      errorMessage: string;
      fieldErrorsByTransactionId: TransactionDrawerFieldErrorsByTransactionId;
    } {
  const failures: DrawerSaveLineValidationFailure[] = [];
  const items: PreparedDrawerSaveItem[] = [];
  const lineCount = payloads.length;

  payloads.forEach((payload, lineIndex) => {
    const { transactionId, original, draft, markAsReviewed } = payload;
    if (!transactionId?.trim()) return;

    let body = buildTransactionSaveBody({
      ...bodyParams,
      original: original as ExcelTransaction,
      draft: draft as ExcelTransaction,
    });
    if (enhanceBody) {
      body = enhanceBody(body, original as ExcelTransaction);
    }

    const missingFieldKeys = validateTransactionEditSaveFieldKeys(
      body,
      validationOptions
    );
    const negativeKeys = listNegativeNetOrGrossKeys(body);
    const missingLabels = missingFieldKeys.map((key) =>
      getTransactionEditRequiredFieldLabel(key)
    );
    if (negativeKeys.length > 0) {
      missingLabels.push(
        encodeNegativeAmountMessage(formatNegativeNetOrGrossMessage(negativeKeys))
      );
    }
    const lineContext = getDrawerSaveLineContextLabel(
      body,
      lineIndex,
      lineCount
    );

    if (missingFieldKeys.length > 0 || negativeKeys.length > 0) {
      const highlightKeys = Array.from(
        new Set<TransactionEditRequiredFieldKey>([
          ...missingFieldKeys,
          ...negativeKeys,
        ])
      );
      failures.push({
        lineContext,
        missingLabels,
        transactionId,
        missingFieldKeys: highlightKeys,
      });
      return;
    }

    items.push({
      transactionUuid: transactionId,
      body,
      markAsReviewed:
        resolveMarkAsReviewed?.(payload) ?? markAsReviewed === true,
    });
  });

  if (failures.length > 0) {
    const fieldErrorsByTransactionId: TransactionDrawerFieldErrorsByTransactionId =
      {};
    for (const failure of failures) {
      fieldErrorsByTransactionId[failure.transactionId] =
        failure.missingFieldKeys;
    }
    return {
      ok: false,
      errorMessage: formatDrawerBatchValidationMessage(failures),
      fieldErrorsByTransactionId,
    };
  }

  if (items.length === 0) {
    return {
      ok: false,
      errorMessage: i18n.t('organisation.no-transactions-to-save'),
      fieldErrorsByTransactionId: {},
    };
  }

  return { ok: true, items };
}

/** Right-hand file list id for filtering transactions to this row's upload bucket. */
export function getTransactionFileFilterId(
  row: ValidatedExcelTransaction | undefined,
  files?: ReadonlyArray<{ excelMetadataId: string; importId?: string | null }>
): string {
  if (!row) return '';
  const r = row as unknown as {
    excelMetadataId?: string | null;
    importId?: string | null;
  };
  const meta = String(r.excelMetadataId ?? '').trim();
  const imp = String(r.importId ?? '').trim();

  if (files?.length) {
    if (meta) {
      const match = files.find(
        (f) =>
          String(f.excelMetadataId ?? '').trim() === meta ||
          String(f.importId ?? '').trim() === meta
      );
      if (match) return String(match.excelMetadataId).trim();
    }
    if (imp) {
      const match = files.find(
        (f) =>
          String(f.excelMetadataId ?? '').trim() === imp ||
          String(f.importId ?? '').trim() === imp
      );
      if (match) return String(match.excelMetadataId).trim();
    }
  }

  return meta || imp;
}

/** All table rows from the same upload (excelMetadataId / importId) as the seed transaction, for one-drawer review. */
export function collectTransactionsSameDocument(
  tableRecords: Record<string, unknown>[],
  seedOriginal: ValidatedExcelTransaction | undefined
): ValidatedExcelTransaction[] {
  if (!seedOriginal?.uuid) return seedOriginal ? [seedOriginal] : [];
  const seedRow = seedOriginal as unknown as {
    excelMetadataId?: string | null;
    importId?: string | null;
  };
  const meta = String(seedRow.excelMetadataId ?? '').trim();
  const imp = String(seedRow.importId ?? '').trim();
  if (!meta && !imp) return [seedOriginal];

  const matchesDocument = (row: ValidatedExcelTransaction): boolean => {
    const rAny = row as unknown as {
      excelMetadataId?: string | null;
      importId?: string | null;
    };
    const rMeta = String(rAny.excelMetadataId ?? '').trim();
    const rImp = String(rAny.importId ?? '').trim();
    return (
      (meta && rMeta === meta) ||
      (imp && rImp === imp) ||
      String(row.uuid) === String(seedOriginal.uuid)
    );
  };

  const result: ValidatedExcelTransaction[] = [];
  const seen = new Set<string>();

  for (const rec of tableRecords) {
    const row = rec.__transactionRow as ValidatedExcelTransaction | undefined;
    if (!row?.uuid) continue;
    const id = String(row.uuid);
    if (!matchesDocument(row) || seen.has(id)) continue;
    seen.add(id);
    result.push(row);
  }

  const seedId = String(seedOriginal.uuid);
  if (!seen.has(seedId)) {
    result.push(seedOriginal);
  }

  return result;
}

/**
 * Load every live line for one uploaded document, independent of the table page size.
 * Uses file `totalRows` when known, then retries with API `totalElements` if the first page was truncated.
 */
export async function fetchAllTransactionsForDocumentFile(params: {
  organisationId: string;
  expectedRowCount?: number | null;
  apiFilters?: Record<string, unknown>;
}): Promise<ValidatedExcelTransaction[]> {
  const { getAllExcelTransactions } = await import('store/organisation/api');
  const { normalizeExcelTransactionFromApi } = await import(
    'store/organisation/reducer/excelHandlers'
  );

  const toRows = (content: ExcelTransaction[]): ValidatedExcelTransaction[] =>
    content.map((row) => ({
      ...normalizeExcelTransactionFromApi(row),
      errorReason: '',
    }));

  const request = async (pageSize: number) => {
    const response = await getAllExcelTransactions(
      params.organisationId,
      1,
      pageSize,
      params.apiFilters
    );
    const content = response?.data?.transactions?.content ?? [];
    const total =
      Number(response?.data?.transactions?.totalElements) || content.length;
    return { content, total };
  };

  const expected =
    params.expectedRowCount != null && params.expectedRowCount > 0
      ? params.expectedRowCount
      : 0;
  const firstSize = Math.max(expected, 500);
  let { content, total } = await request(firstSize);
  if (total > content.length) {
    ({ content } = await request(total));
  }
  return toRows(content);
}

/** Distinct uploaded document / import bucket for navigation (aligned with collectTransactionsSameDocument). */
export function getTransactionDocumentNavigationKey(
  row: ValidatedExcelTransaction | undefined
): string {
  if (!row?.uuid) return '';
  const rAny = row as unknown as {
    excelMetadataId?: string | null;
    importId?: string | null;
  };
  const meta = String(rAny.excelMetadataId ?? '').trim();
  const imp = String(rAny.importId ?? '').trim();
  if (meta) return `m:${meta}`;
  if (imp) return `i:${imp}`;
  return `u:${String(row.uuid)}`;
}

/** First visible row per document, in screen order — for prev/next between documents. */
export function documentRepresentativesTableOrder(
  tableRecords: Record<string, unknown>[]
): Record<string, unknown>[] {
  const seen = new Set<string>();
  const reps: Record<string, unknown>[] = [];
  for (const rec of tableRecords) {
    const orig = rec.__transactionRow as ValidatedExcelTransaction | undefined;
    const k = getTransactionDocumentNavigationKey(orig);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    reps.push(rec);
  }
  return reps;
}

export function buildDrawerNavigationTableRecords(
  rows: ValidatedExcelTransaction[] | undefined
): Record<string, unknown>[] {
  if (!rows?.length) return [];
  return rows.map((row) => ({
    __transactionUuid: row.uuid,
    __transactionRow: row,
  }));
}

export type DrawerDocumentNavigationState = {
  hasPrevious: boolean;
  hasNext: boolean;
  navigationMode: 'transaction' | 'document';
  onNavigateToPrevious: () => void;
  onNavigateToNext: () => void;
};

/** Prev/next between documents or Excel rows using the current table page snapshot. */
export function computeDrawerDocumentNavigation(params: {
  tableRecords: Record<string, unknown>[];
  attachmentsTransactionId: string;
  seedOriginal: ValidatedExcelTransaction | undefined;
  navigateToRecord: (record: Record<string, unknown>) => void;
}): DrawerDocumentNavigationState {
  const {
    tableRecords,
    attachmentsTransactionId,
    seedOriginal,
    navigateToRecord,
  } = params;
  const original = seedOriginal;
  const isExcelTransactionRow = original?.isExcelTransaction === true;

  if (isExcelTransactionRow) {
    const currentIndex = tableRecords.findIndex(
      (r) =>
        String(r.__transactionUuid ?? '') === String(attachmentsTransactionId)
    );
    return {
      hasPrevious: currentIndex > 0,
      hasNext: currentIndex >= 0 && currentIndex < tableRecords.length - 1,
      navigationMode: 'transaction',
      onNavigateToPrevious: () => {
        if (currentIndex <= 0) return;
        navigateToRecord(tableRecords[currentIndex - 1]);
      },
      onNavigateToNext: () => {
        if (currentIndex < 0 || currentIndex >= tableRecords.length - 1) {
          return;
        }
        navigateToRecord(tableRecords[currentIndex + 1]);
      },
    };
  }

  const docReps = documentRepresentativesTableOrder(tableRecords);
  const currentTxRow = tableRecords.find(
    (r) =>
      String(r.__transactionUuid ?? '') === String(attachmentsTransactionId)
  );
  const currentOrigNav = currentTxRow?.__transactionRow as
    | ValidatedExcelTransaction
    | undefined;
  const currentNavKey = getTransactionDocumentNavigationKey(currentOrigNav);
  const docNavIndex =
    currentNavKey !== ''
      ? docReps.findIndex((r) => {
          const o = r.__transactionRow as ValidatedExcelTransaction | undefined;
          return getTransactionDocumentNavigationKey(o) === currentNavKey;
        })
      : -1;

  return {
    hasPrevious: docNavIndex > 0,
    hasNext: docNavIndex >= 0 && docNavIndex < docReps.length - 1,
    navigationMode: 'document',
    onNavigateToPrevious: () => {
      if (docNavIndex <= 0) return;
      navigateToRecord(docReps[docNavIndex - 1]);
    },
    onNavigateToNext: () => {
      if (docNavIndex < 0 || docNavIndex >= docReps.length - 1) return;
      navigateToRecord(docReps[docNavIndex + 1]);
    },
  };
}

/** Receipts entities linked to QuickBooks show the QB logo on the attachment strip. */
export function resolveAttachmentViewerProviderLogoKey(
  organisation: Organisation | undefined
): string {
  const main = organisation?.providerName ?? '';
  if (!organisation) return main;
  if (main.toLowerCase() === Provider.RECEIPTS.toLowerCase()) {
    const linked = organisation.groupedConnections?.find((c) => {
      const n = (c.providerName ?? '').toLowerCase();
      return n === Provider.QUICKBOOKS || n.includes('quickbook');
    });
    if (linked) return Provider.QUICKBOOKS;
  }
  return main;
}

/** Open provider from file metadata match (Receipts/QBO links). */
export function resolveProviderExternalLinkForDocumentRows(
  organisation: Organisation | undefined,
  rows: ValidatedExcelTransaction[]
): string {
  const metaList = organisation?.excelFilesMetadata;
  if (!metaList?.length || !rows.length) return '';
  const seed = rows[0] as unknown as {
    excelMetadataId?: string | null;
    importId?: string | null;
  };
  const metaId = String(seed.excelMetadataId ?? '').trim();
  const impId = String(seed.importId ?? '').trim();
  const file = metaList.find(
    (f) =>
      (metaId && f.excelMetadataId === metaId) ||
      (impId && f.importId === impId)
  );
  const link = file?.linkToProvider?.trim();
  return link ?? '';
}

const SALES_SOURCE_TYPES_FOR_CONTACT_FILTER = new Set([
  'Sales - Invoice / Receipt',
  'Sales Credit Note',
  'SALES_INVOICE_RECEIPT',
  'SALES_CREDIT_NOTE',
]);

const PURCHASE_SOURCE_TYPES_FOR_CONTACT_FILTER = new Set([
  'Purchase - Supplier Bill / Payment',
  'Purchase Credit Note',
  'PURCHASE_SUPPLIER_BILL_PAYMENT',
  'PURCHASE_CREDIT_NOTE',
]);

export type SourceTypeContactFilterCategory = 'sales' | 'purchase';

/** Prefer taxRatesUnique when populated (e.g. QuickBooks); otherwise organisation.taxRates. */
export function resolveOrganisationTaxRatesList(
  organisation?: Organisation | null
): TaxRate[] {
  if (!organisation) return [];
  if (organisation.taxRatesUnique?.length) {
    return organisation.taxRatesUnique;
  }
  return organisation.taxRates ?? [];
}

/** Saved/configured rates for setup checklists — prefer taxRates (post-save) over taxRatesUnique. */
export function resolveOrganisationTaxRatesForSetupCheck(
  organisation?: Organisation | null
): TaxRate[] {
  if (!organisation) return [];
  if (organisation.taxRates?.length) {
    return organisation.taxRates;
  }
  if (organisation.taxRatesUnique?.length) {
    return organisation.taxRatesUnique;
  }
  return [];
}

export function taxRatesListHasNonZeroRate(
  rates: TaxRate[] | undefined | null
): boolean {
  if (!rates?.length) return false;
  return rates.some((rate) => {
    const raw = rate?.taxRate ?? (rate as { rate?: number | string }).rate;
    const num = typeof raw === 'number' ? raw : raw != null ? Number(raw) : 0;
    return !Number.isNaN(num) && num !== 0;
  });
}

export { isQuickBooksOrZohoProvider };

/** Maps transaction source type to sales (customer) vs purchase (supplier) for QB/Zoho contact filtering. */
export function getSourceTypeContactFilterCategory(
  sourceType: string | null | undefined
): SourceTypeContactFilterCategory | null {
  const t = String(sourceType ?? '').trim();
  if (!t) return null;
  if (SALES_SOURCE_TYPES_FOR_CONTACT_FILTER.has(t)) return 'sales';
  if (PURCHASE_SOURCE_TYPES_FOR_CONTACT_FILTER.has(t)) return 'purchase';
  const lower = t.toLowerCase();
  if (lower.includes('sales')) return 'sales';
  if (lower.includes('purchase') || lower.includes('supplier')) {
    return 'purchase';
  }
  return null;
}

export function viesContactMatchesSourceTypeCategory(
  contactType: string | null | undefined,
  category: SourceTypeContactFilterCategory
): boolean {
  if (!contactType?.trim()) return true;
  const ct = ContactType[contactType as keyof typeof ContactType];
  if (ct === undefined) return true;
  if (category === 'sales') {
    return ct === ContactType.CUSTOMER || ct === ContactType.BOTH;
  }
  return ct === ContactType.SUPPLIER || ct === ContactType.BOTH;
}

/** QuickBooks/Zoho: limit Name dropdown contacts by row source type; other providers unchanged. */
export function filterContactOptionsForSourceType(
  options: { label: string; value: string }[],
  contactTypeByContactId: Map<string, string>,
  sourceType: string | null | undefined,
  providerName: string | null | undefined,
  pinnedContactId?: string | null
): { label: string; value: string }[] {
  if (!isQuickBooksOrZohoProvider(providerName)) {
    return options;
  }
  const category = getSourceTypeContactFilterCategory(sourceType);
  if (!category) {
    return options;
  }
  const pinned = String(pinnedContactId ?? '').trim();
  const filtered = options.filter((opt) => {
    const ct = contactTypeByContactId.get(String(opt.value).trim());
    if (!ct) return true;
    return viesContactMatchesSourceTypeCategory(ct, category);
  });
  if (!pinned) {
    return filtered;
  }
  const pinnedOpt = options.find((o) => String(o.value).trim() === pinned);
  if (pinnedOpt && !filtered.some((o) => o.value === pinnedOpt.value)) {
    return [...filtered, pinnedOpt];
  }
  return filtered;
}

/** Mass update Name: filter only when every selected row shares the same sales/purchase category. */
export function resolveMassUpdateContactOptionsForSourceTypes(
  options: { label: string; value: string }[],
  contactTypeByContactId: Map<string, string>,
  sourceTypes: Array<string | null | undefined>,
  providerName: string | null | undefined
): { label: string; value: string }[] {
  if (!isQuickBooksOrZohoProvider(providerName)) {
    return options;
  }
  const categories = new Set<SourceTypeContactFilterCategory>();
  for (const st of sourceTypes) {
    const cat = getSourceTypeContactFilterCategory(st);
    if (cat) categories.add(cat);
  }
  if (categories.size !== 1) {
    return options;
  }
  const category = [...categories][0];
  return options.filter((opt) => {
    const ct = contactTypeByContactId.get(String(opt.value).trim());
    if (!ct) return true;
    return viesContactMatchesSourceTypeCategory(ct, category);
  });
}

let receiptTaxEditWarningShown = false;

/** Client-only id prefix for unsaved manual table rows (not persisted yet). */
export const MANUAL_ENTRY_CLIENT_ID_PREFIX = 'manual-entry-';

export function isClientOnlyManualTransactionId(
  id: string | null | undefined
): boolean {
  return String(id ?? '').startsWith(MANUAL_ENTRY_CLIENT_ID_PREFIX);
}

/** Blank Excel journal row for inline "Add a Transaction" before POST create. */
export function createBlankManualExcelTransaction(
  overrides?: Partial<ValidatedExcelTransaction>
): ValidatedExcelTransaction {
  const clientId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? `${MANUAL_ENTRY_CLIENT_ID_PREFIX}${crypto.randomUUID()}`
      : `${MANUAL_ENTRY_CLIENT_ID_PREFIX}${Date.now()}`;

  return {
    uuid: clientId,
    accountId: null,
    accountName: null,
    accountType: null,
    contactHashRow: null,
    contactId: null,
    contactName: '',
    country: '',
    currency: '',
    description: '',
    reference: '',
    sourceType: '',
    taxName: '',
    transactionDate: '',
    transactionNumber: '',
    gross: 0,
    net: 0,
    tax: 0,
    taxRate: 0,
    taxRateId: null,
    vatNumber: null,
    hashRow: null,
    locked: false,
    hasAttachment: false,
    reviewStatus: ReviewStatus.NOT_REVIEWED,
    isExcelTransaction: true,
    reversalType: 'NONE',
    taxInclusive: true,
    quantity: null,
    unitPrice: null,
    discountAmount: null,
    errorReason: '',
    ...overrides,
  };
}

/** One-time warning when editing tax on Xero / QuickBooks / Zoho receipt lines. */
export function notifyReceiptTaxEditWarning(params: {
  enabled: boolean;
  dispatch: (action: unknown) => void;
  t: TFunction;
}): void {
  if (!params.enabled || receiptTaxEditWarningShown) return;
  receiptTaxEditWarningShown = true;
  params.dispatch(
    turnMessageOn({
      title: params.t('organisation.transactions-tax-edit-receipt-warning-title'),
      description: params.t(
        'organisation.transactions-tax-edit-receipt-warning-description'
      ),
      type: MessageStates.WARNING,
    })
  );
}
